import useAttendeeStatus from '@hooks/sdk/useAttendeeStatus';
import useVolumeIndicator from '@hooks/sdk/useVolumeIndicator';
import { type HTMLAttributes } from 'react';
import type { VideoTileStyleProps } from './config';
import {
  Container,
  Dot,
  DotWrap,
  NameplatePeerScreen,
  NameplateScreen,
  NameplateScreenName,
} from './style';
import MicOffIcon from '/icon/micOff.svg';

type ObjectFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
type VideoTileType = 'my' | 'peer' | 'candidate' | 'preview' | 'interviewer';

interface MicStatusProps {
  attendeeId: string;
  muted: boolean;
}

const MicStatus = ({ attendeeId, muted }: MicStatusProps) => {
  const volume = useVolumeIndicator(attendeeId);

  return (
    <div className='ch-status'>
      {muted ? (
        <div className='ch-status-icon'>
          <img src={MicOffIcon} alt={'마이크 꺼짐'} />
        </div>
      ) : (
        <DotWrap>
          <Dot volume={volume > 0 ? 2 : 1} />
          <Dot volume={volume > 0 ? 3 : 1} />
          <Dot volume={volume > 0 ? 2 : 1} />
        </DotWrap>
      )}
    </div>
  );
};

export interface VideoTileProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'css'>, VideoTileStyleProps {
  attendeeId: string;
  /** The name to show on the video tile */
  nameplate?: string;
  /** Specifies which CSS object-fit value to apply to the VideoTile so that it resizes to fit its container  */
  objectFit?: ObjectFit;
  videoTileType?: VideoTileType;
  ref?: React.Ref<HTMLVideoElement>;
}

const VideoTile = (props: VideoTileProps) => {
  const { attendeeId, tag, className, nameplate, videoTileType, ref, ...rest } =
    props;
  const { muted, videoEnabled } = useAttendeeStatus(attendeeId);

  const getNameplateScreenEl = () => {
    switch (videoTileType) {
      case 'peer':
        return (
          <NameplatePeerScreen className='ch-nameplate-screen'>
            {/*{nameplate}*/}
          </NameplatePeerScreen>
        );
      case 'candidate':
      case 'interviewer':
        return (
          <NameplateScreen className='ch-nameplate-screen'>
            <NameplateScreenName className='ch-nameplate-screen-name'>
              {nameplate}
            </NameplateScreenName>
          </NameplateScreen>
        );
    }
  };

  return (
    <Container
      as={tag}
      className={className || ''}
      data-testid='video-tile'
      {...rest}
    >
      <video ref={ref} className='ch-video' />
      {!videoEnabled && getNameplateScreenEl()}
      {videoTileType !== 'my' && (
        <MicStatus attendeeId={attendeeId} muted={muted} />
      )}
      {nameplate && (
        <div className='ch-nameplate'>
          <p className='ch-text' title={nameplate}>
            {nameplate}
          </p>
        </div>
      )}
    </Container>
  );
};

export default VideoTile;
